import { useClerk, useAuth, useSignIn, useSignUp } from "@clerk/react";
import { usePostHog } from "@posthog/react";
import sillLogo from "@sill/shared/assets/logo.svg";
import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

import { JoinReveal } from "../components/join/JoinReveal";
import { JoinSelect } from "../components/join/JoinSelect";
import { OneTimeCodeField } from "../components/join/OneTimeCodeField";
import { ServiceAreaField } from "../components/join/ServiceAreaField";
import { Button } from "../components/primitives/Button";
import { TextField } from "../components/primitives/Field";
import { ApiError } from "../lib/api";
import { captureEvent } from "../lib/capture-event";
import {
  isProRole,
  realJoinApi,
  type JoinApi,
  type JoinAuth,
  type JoinCodeMode,
  type JoinPolicy,
  type JoinRequest,
  type JoinRequirementsResponse,
  type JoinResponse,
} from "../lib/join-api";
import {
  joinRejected,
  joinSubmitted,
  joinSucceeded,
  joinViewed,
} from "../lib/join-analytics";
import {
  GENERIC_JOIN_MESSAGE,
  INVALID_CAMPAIGN_CREDENTIAL_MESSAGE,
  POLICY_UNAVAILABLE_MESSAGE,
  REQUIREMENTS_UNAVAILABLE_MESSAGE,
  joinRejectMessage,
} from "../lib/join-copy";
import {
  previewJoinApi,
  previewJoinAuth,
  previewJoinState,
  type JoinPreviewState,
} from "../lib/join-preview";
import { FIRST_CONNECTION_FREE_LINE } from "../lib/first-connection-promo";
import { DEFAULT_LICENSE_STATE } from "../lib/license";
import { IS_PREVIEW } from "../lib/pro-mode";
import { useLenderFlagOn } from "../hooks/useLenderMarketplaceGate";
import { PRO_ROUTE_PATHS } from "../lib/routes";

// Mirrors proadmission.MaxServiceAreaLength: the server refuses a longer value
// before resolving it, so the input stops at the same bound.
const SERVICE_AREA_MAX_LENGTH = 100;

/** Where a successful join lands: the offer step for an in-area agent, else Home. */
export function postJoinPath(result: Pick<JoinResponse, "pro_type" | "service_area_launched">): string {
  if (result.pro_type === "agent" && result.service_area_launched !== false) {
    return PRO_ROUTE_PATHS.joinOffer;
  }
  // The preview has no backend to tell Home the area is out: carry it in the URL.
  return IS_PREVIEW && result.service_area_launched === false
    ? `${PRO_ROUTE_PATHS.home}?area=out`
    : PRO_ROUTE_PATHS.home;
}

/**
 * Pro signup (`/join`, plan 476 M3): form -> validate -> Clerk email-code
 * account -> join. Public route, outside the authenticated shell. Under
 * IS_PREVIEW there is no ClerkProvider, so Clerk's hooks must not run: the
 * preview branch feeds the same flow fixtures instead.
 */
export function JoinPage() {
  if (IS_PREVIEW) {
    const state = previewJoinState();
    return (
      <JoinFlow
        auth={previewJoinAuth}
        api={previewJoinApi(state)}
        preview={state}
      />
    );
  }
  return <ClerkJoinPage />;
}

function ClerkJoinPage() {
  const { isLoaded, isSignedIn, getToken } = useAuth();
  const { signUp } = useSignUp();
  const { signIn } = useSignIn();
  const clerk = useClerk();

  const auth = useMemo<JoinAuth>(
    () => ({
      isLoaded,
      isSignedIn: Boolean(isSignedIn),
      getToken: async () => (await clerk.session?.getToken()) ?? (await getToken()),
      startSignUp: async ({ email, firstName, lastName }) => {
        const { error } = await signUp.create({
          emailAddress: email,
          firstName: firstName || undefined,
          lastName: lastName || undefined,
        });
        if (error) {
          if (error.code === "form_identifier_exists") return { kind: "exists" };
          return { kind: "error", message: error.message ?? "Account creation failed" };
        }
        const sent = await signUp.verifications.sendEmailCode();
        if (sent.error) {
          return { kind: "error", message: sent.error.message ?? "Could not send the code" };
        }
        return { kind: "code_sent" };
      },
      startSignIn: async (email) => {
        const created = await signIn.create({ identifier: email });
        if (created.error) {
          return { kind: "error", message: created.error.message ?? "Could not start sign-in" };
        }
        const sent = await signIn.emailCode.sendCode();
        if (sent.error) {
          return { kind: "error", message: sent.error.message ?? "Could not send the code" };
        }
        return { kind: "code_sent" };
      },
      verify: async (mode: JoinCodeMode, code) => {
        if (mode === "signup") {
          const verified = await signUp.verifications.verifyEmailCode({ code });
          if (verified.error) {
            return { ok: false, message: verified.error.message ?? "Invalid code. Try again." };
          }
          const done = await signUp.finalize();
          if (done.error) {
            return { ok: false, message: done.error.message ?? "Could not finish sign-up" };
          }
          return { ok: true };
        }
        const verified = await signIn.emailCode.verifyCode({ code });
        if (verified.error) {
          return { ok: false, message: verified.error.message ?? "Invalid code. Try again." };
        }
        if (signIn.status !== "complete") {
          // A second factor is required: that belongs to the regular sign-in page.
          return {
            ok: false,
            needsLogin: true,
            message: "Your account needs one more step to sign in.",
          };
        }
        const done = await signIn.finalize();
        if (done.error) {
          return { ok: false, message: done.error.message ?? "Could not sign you in" };
        }
        return { ok: true };
      },
    }),
    // The signal-API resources are stable objects whose state mutates in place.
    [isLoaded, isSignedIn, getToken, clerk, signUp, signIn],
  );

  return <JoinFlow auth={auth} api={realJoinApi} />;
}

interface FormState {
  code: string;
  email: string;
  firstName: string;
  lastName: string;
  brokerage: string;
  proType: "" | "agent" | "lo";
  serviceArea: string;
  /** Set only while `serviceArea` is exactly a picked Places suggestion. */
  serviceAreaPlaceId: string;
  licenseNumber: string;
  licenseState: string;
  nmlsNumber: string;
}

const INITIAL_FORM: FormState = {
  code: "",
  email: "",
  firstName: "",
  lastName: "",
  brokerage: "",
  proType: "",
  serviceArea: "",
  serviceAreaPlaceId: "",
  licenseNumber: "",
  licenseState: DEFAULT_LICENSE_STATE,
  nmlsNumber: "",
};

type Step = "form" | "code" | "working";
type PolicyState =
  | { status: "loading" }
  | { status: "error" }
  | { status: "ready"; policy: JoinPolicy };

export interface JoinFlowProps {
  auth: JoinAuth;
  api: JoinApi;
  /** IS_PREVIEW only: seeds the step/message a fixture state starts in. */
  preview?: JoinPreviewState;
}

export function JoinFlow({ auth, api, preview }: JoinFlowProps) {
  const navigate = useNavigate();
  const posthog = usePostHog();
  const [searchParams] = useSearchParams();

  // The visitor has no role yet, so this is the raw launch flag (loading = off).
  // Off: lenders cannot sign up here, so there is no picker and the form is agent.
  const lenderOption = useLenderFlagOn();

  // Opaque attribution inputs, parsed once at mount. Never rendered or logged;
  // the campaign credential is also stripped from the address bar below.
  // Held in state: the strip effect below removes it from the address bar, and
  // router state must not be the only place it lives.
  const [proReferralToken] = useState(() => (searchParams.get("pro_referral_token") ?? "").trim());
  const [campaignCredential, setCampaignCredential] = useState(() =>
    (searchParams.get("campaign") ?? "").trim(),
  );

  const [form, setForm] = useState<FormState>(() => {
    const code = (searchParams.get("code") ?? "").trim().toUpperCase();
    const base = code ? { ...INITIAL_FORM, code } : INITIAL_FORM;
    return preview === "code" ? { ...base, email: "you@example.com" } : base;
  });
  const [step, setStep] = useState<Step>(preview === "code" ? "code" : "form");
  const [codeMode, setCodeMode] = useState<JoinCodeMode>("signup");
  const [oneTimeCode, setOneTimeCode] = useState("");
  const [message, setMessage] = useState<string | null>(() =>
    preview === "refusal"
      ? joinRejectMessage("professional_credential_conflict", "re_license", {
          licenseNumber: "SA-12345",
          nmlsNumber: "",
        })
      : null,
  );
  const [notice, setNotice] = useState<string | null>(null);
  // True once this page holds a session (verified here, or already signed in).
  const [accountReady, setAccountReady] = useState(false);

  const [policy, setPolicy] = useState<PolicyState>({ status: "loading" });
  const [policyAttempt, setPolicyAttempt] = useState(0);
  const [requirements, setRequirements] = useState<JoinRequirementsResponse | null>(null);
  const [requirementsStatus, setRequirementsStatus] = useState<
    "idle" | "checking" | "ready" | "error"
  >("idle");

  // A signed-in visitor with a pro role has nothing to join: Home.
  const [roleChecked, setRoleChecked] = useState(false);
  const signedIn = auth.isSignedIn || accountReady;

  // ---- analytics: viewed once per mount (StrictMode double-effect safe) ----
  const viewedRef = useRef(false);
  useEffect(() => {
    if (viewedRef.current) return;
    viewedRef.current = true;
    captureEvent(posthog, joinViewed());
  }, [posthog]);

  // ---- strip credentials from the address bar (credential secrecy) ----
  // ?campaign=, ?pro_referral_token= and ?code= are read into state first, then
  // removed so PostHog $current_url / Sentry never see them.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    const SECRET_PARAMS = ["campaign", "pro_referral_token", "code"];
    if (!SECRET_PARAMS.some((k) => params.has(k))) return;
    for (const k of SECRET_PARAMS) params.delete(k);
    const qs = params.toString();
    window.history.replaceState(
      null,
      "",
      `${window.location.pathname}${qs ? `?${qs}` : ""}${window.location.hash}`,
    );
  }, []);

  // ---- signed-in pro: straight to Home (role from /api/users/me) ----
  useEffect(() => {
    if (!auth.isLoaded || !auth.isSignedIn) return;
    let active = true;
    void (async () => {
      try {
        const token = await auth.getToken();
        const role = token ? await api.role(token) : null;
        if (!active) return;
        if (isProRole(role)) {
          navigate(PRO_ROUTE_PATHS.home, { replace: true });
          return;
        }
      } catch {
        // Unreadable role: fall through to the form; the join call is idempotent.
      }
      if (active) setRoleChecked(true);
    })();
    return () => {
      active = false;
    };
    // `auth` is re-created by Clerk state changes; only its settled flags matter.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.isLoaded, auth.isSignedIn]);

  // ---- policy (retry state on failure, never a code field) ----
  useEffect(() => {
    let active = true;
    void api
      .policy()
      .then((result) => {
        if (active) setPolicy({ status: "ready", policy: result.policy });
      })
      .catch(() => {
        if (active) setPolicy({ status: "error" });
      });
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [policyAttempt]);

  const policyKind = policy.status === "ready" ? policy.policy : null;
  const showCode =
    policyKind === "code_only" ||
    (policyKind !== null &&
      !proReferralToken &&
      (policyKind !== "open" || Boolean(form.code.trim())));
  // Without the lender option the only role is agent, so the picker is gone.
  const proType: FormState["proType"] = lenderOption ? form.proType : "agent";
  const sponsored = Boolean(proReferralToken) && policyKind !== null && policyKind !== "code_only";
  const serviceAreaRequired =
    policyKind === "open" && !form.code.trim() && !proReferralToken;

  // ---- requirements preflight (which credential field to show) ----
  useEffect(() => {
    if (!policyKind) return;
    const code = form.code.trim().toUpperCase();
    if ((showCode && !code) || (!showCode && !proType)) return;
    let active = true;
    const timer = window.setTimeout(() => {
      setRequirements(null);
      setRequirementsStatus("checking");
      void api
        .requirements({
          ...(showCode ? { code } : { pro_type: proType as "agent" | "lo" }),
          ...(proReferralToken ? { pro_referral_token: proReferralToken } : {}),
          ...(campaignCredential ? { campaign_credential: campaignCredential } : {}),
        })
        .then((result) => {
          if (!active) return;
          setRequirements(result);
          setRequirementsStatus("ready");
        })
        .catch((error: unknown) => {
          if (!active) return;
          if (error instanceof ApiError && error.code === "invalid_campaign_credential") {
            // Drop only the rejected credential; this effect reruns through the ordinary lane.
            setCampaignCredential("");
            setRequirements(null);
            setRequirementsStatus("idle");
            setMessage(INVALID_CAMPAIGN_CREDENTIAL_MESSAGE);
            return;
          }
          setRequirements(null);
          setRequirementsStatus("error");
        });
    }, 200);
    return () => {
      active = false;
      window.clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignCredential, form.code, proType, policyKind, proReferralToken, showCode]);

  function update(field: keyof FormState, value: string) {
    setForm((current) => ({ ...current, [field]: value }));
    if (field === "code" || field === "proType") {
      setRequirements(null);
      setRequirementsStatus("idle");
    }
    setMessage(null);
  }

  function buildRequest(): JoinRequest {
    const serviceArea = form.serviceArea.trim();
    return {
      first_name: form.firstName.trim(),
      last_name: form.lastName.trim(),
      brokerage: form.brokerage.trim(),
      ...(showCode ? { code: form.code.trim().toUpperCase() } : {}),
      ...(showCode ? {} : { pro_type: proType as "agent" | "lo" }),
      ...(proReferralToken ? { pro_referral_token: proReferralToken } : {}),
      ...(campaignCredential ? { campaign_credential: campaignCredential } : {}),
      ...(serviceArea ? { service_area: serviceArea } : {}),
      ...(serviceArea && form.serviceAreaPlaceId
        ? { service_area_place_id: form.serviceAreaPlaceId }
        : {}),
      ...(requirements?.credential_kind === "re_license" && form.licenseNumber.trim()
        ? {
            license_num: form.licenseNumber.trim(),
            license_state: form.licenseState.trim().toUpperCase() || DEFAULT_LICENSE_STATE,
          }
        : {}),
      ...(requirements?.credential_kind === "nmls" && form.nmlsNumber.trim()
        ? { nmls_number: form.nmlsNumber.trim() }
        : {}),
    };
  }

  /** Reports a refusal inline, once, and returns the visitor to the form. */
  function reject(error: unknown, back: Step = "form") {
    const code = error instanceof ApiError ? error.code : undefined;
    captureEvent(posthog, joinRejected(code));
    if (code === "invalid_campaign_credential") setCampaignCredential("");
    const text = joinRejectMessage(code, requirements?.credential_kind, {
      licenseNumber: form.licenseNumber,
      nmlsNumber: form.nmlsNumber,
    });
    setMessage(
      text ??
        (error instanceof ApiError && error.message ? error.message : GENERIC_JOIN_MESSAGE),
    );
    setStep(back);
  }

  async function finishJoin() {
    setStep("working");
    try {
      const token = await auth.getToken();
      if (!token) {
        setMessage("We couldn't confirm your sign-in. Try again.");
        setStep("form");
        return;
      }
      const result = await api.join(buildRequest(), token);
      captureEvent(posthog, joinSucceeded(result.lane));
      navigate(postJoinPath(result), { replace: true });
    } catch (error) {
      reject(error);
    }
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setMessage(null);
    setNotice(null);
    captureEvent(posthog, joinSubmitted());
    setStep("working");
    const email = form.email.trim();
    try {
      // A refusal here creates no account: validate runs before any Clerk call.
      await api.validate({ ...buildRequest(), ...(signedIn ? {} : { email }) });
    } catch (error) {
      reject(error);
      return;
    }
    if (signedIn) {
      await finishJoin();
      return;
    }
    try {
      const first = await auth.startSignUp({
        email,
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
      });
      if (first.kind === "code_sent") {
        setCodeMode("signup");
        setStep("code");
        return;
      }
      if (first.kind === "exists") {
        // An existing Sill/Clerk account for this address: sign in with an
        // emailed code instead, then join.
        const second = await auth.startSignIn(email);
        if (second.kind === "code_sent") {
          setCodeMode("signin");
          setNotice("You already have a Sill account with this email. We emailed you a sign-in code.");
          setStep("code");
          return;
        }
        setMessage(second.kind === "error" ? second.message : GENERIC_JOIN_MESSAGE);
        setStep("form");
        return;
      }
      setMessage(first.message);
      setStep("form");
    } catch {
      setMessage(GENERIC_JOIN_MESSAGE);
      setStep("form");
    }
  }

  async function handleVerify(event: FormEvent) {
    event.preventDefault();
    setMessage(null);
    setStep("working");
    try {
      const result = await auth.verify(codeMode, oneTimeCode.trim());
      if (!result.ok) {
        if (result.needsLogin) {
          navigate("/login", { replace: true });
          return;
        }
        setMessage(result.message);
        setStep("code");
        return;
      }
      setAccountReady(true);
      await finishJoin();
    } catch {
      setMessage(GENERIC_JOIN_MESSAGE);
      setStep("code");
    }
  }

  // Role known at first render (flag off): the promo renders in place; a role
  // picked later reveals it instead, so nothing above shifts.
  const [agentAtMount] = useState(() => proType === "agent");
  const promoLine = (
    <p data-testid="join-promo-line" className="mx-auto mt-1 max-w-[380px] text-center text-sm font-semibold text-fg">
      {FIRST_CONNECTION_FREE_LINE}
    </p>
  );
  const checking = !auth.isLoaded || (auth.isSignedIn && !roleChecked);

  return (
    <main className="flex min-h-screen items-center justify-center bg-bg-warm p-4">
      <div className="w-full max-w-[460px] py-8">
        <img src={sillLogo} alt="Sill" className="mx-auto mb-6 h-10 w-auto" />
        <h1 className="text-center font-display text-[28px] font-semibold leading-tight tracking-[-0.015em] text-fg">Join Sill for Pros</h1>
        <p className="view-sub mx-auto mt-2 max-w-[380px] text-center">
          Your CRM is ready the moment you sign up. No invite email to wait for.
        </p>
        {/* Agents only: lenders never receive the promo (PromoIssuer is agent-only). */}
        {proType === "agent" ? (
          agentAtMount ? (
            promoLine
          ) : (
            <JoinReveal>{promoLine}</JoinReveal>
          )
        ) : null}

        <div className="mt-6 rounded-2xl border border-border bg-card p-5 shadow-card">
          {checking || step === "working" ? (
            <div role="status" aria-live="polite" className="py-6 text-center text-sm text-fg-muted">
              {step === "working" ? "Setting up your account…" : "Loading…"}
            </div>
          ) : policy.status === "loading" ? (
            <div role="status" className="py-6 text-center text-sm text-fg-muted">
              Checking your signup options…
            </div>
          ) : policy.status === "error" ? (
            <div role="alert" data-testid="join-policy-retry" className="space-y-3 py-2 text-center">
              <p className="text-sm text-fg">{POLICY_UNAVAILABLE_MESSAGE}</p>
              <Button
                onClick={() => {
                  setPolicy({ status: "loading" });
                  setPolicyAttempt((n) => n + 1);
                }}
              >
                Try again
              </Button>
            </div>
          ) : step === "code" ? (
            <form onSubmit={handleVerify} className="space-y-4" data-testid="join-code-form">
              {notice ? <p className="text-sm text-fg-muted">{notice}</p> : null}
              <p className="text-sm text-fg-muted">
                We sent a six-digit code to <span className="font-medium text-fg">{form.email.trim()}</span>.
              </p>
              <OneTimeCodeField value={oneTimeCode} onChange={setOneTimeCode} />
              <Button type="submit" disabled={oneTimeCode.trim().length === 0} className="w-full">
                Verify and continue
              </Button>
              {message ? (
                <p role="alert" className="text-sm text-destructive">
                  {message}
                </p>
              ) : null}
            </form>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="join-form">
              {sponsored ? (
                <p className="rounded-xl bg-pro-soft px-3 py-2 text-sm text-fg">
                  This referral includes sponsored admission.
                </p>
              ) : null}
              {showCode ? (
                <JoinField
                  id="join-admission-code"
                  label="Admission code"
                  value={form.code}
                  onChange={(v) => update("code", v.toUpperCase())}
                  placeholder="SILL-ABCD-1234"
                  required
                />
              ) : lenderOption ? (
                <div>
                  <label htmlFor="join-pro-type" className="field-label">
                    Professional role
                  </label>
                  <JoinSelect
                    id="join-pro-type"
                    value={form.proType}
                    required
                    onChange={(e) => update("proType", e.target.value)}
                  >
                    <option value="">Select agent or lender</option>
                    <option value="agent">Real estate agent</option>
                    <option value="lo">Mortgage lender</option>
                  </JoinSelect>
                </div>
              ) : null}
              {signedIn ? null : (
                <JoinField
                  id="join-email"
                  label="Email"
                  type="email"
                  value={form.email}
                  onChange={(v) => update("email", v)}
                  placeholder="you@example.com"
                  required
                />
              )}
              <div className="grid gap-3 sm:grid-cols-2">
                <JoinField
                  id="join-first-name"
                  label="First name"
                  value={form.firstName}
                  onChange={(v) => update("firstName", v)}
                  placeholder="First name"
                />
                <JoinField
                  id="join-last-name"
                  label="Last name"
                  value={form.lastName}
                  onChange={(v) => update("lastName", v)}
                  placeholder="Last name"
                />
              </div>
              <JoinField
                id="join-brokerage"
                label="Brokerage"
                value={form.brokerage}
                onChange={(v) => update("brokerage", v)}
                placeholder="Brokerage"
              />
              <ServiceAreaField
                id="join-service-area"
                label="Primary service area"
                value={form.serviceArea}
                onChange={(value, placeId) => {
                  setForm((current) => ({
                    ...current,
                    serviceArea: value,
                    serviceAreaPlaceId: placeId ?? "",
                  }));
                  setMessage(null);
                }}
                placeholder="City, neighborhood, or ZIP"
                required={serviceAreaRequired}
                maxLength={SERVICE_AREA_MAX_LENGTH}
              />
              {requirements?.credential_kind === "re_license" ? (
                <JoinReveal testId="join-credential-reveal">
                <div className="space-y-3 rounded-xl border border-border/70 bg-bg p-3">
                  <div className="grid gap-3 sm:grid-cols-[1fr_88px]">
                    <JoinField
                      id="join-license"
                      label="Real estate license"
                      value={form.licenseNumber}
                      onChange={(v) => update("licenseNumber", v)}
                      placeholder="SA-12345"
                      required={requirements.credential_required}
                    />
                    <JoinField
                      id="join-license-state"
                      label="State"
                      value={form.licenseState}
                      onChange={(v) => update("licenseState", v.toUpperCase())}
                      placeholder="WA"
                      maxLength={2}
                      required={requirements.credential_required}
                    />
                  </div>
                  <p className="text-xs text-fg-muted">
                    We use this to prevent duplicate professional accounts. Verification
                    happens after signup.
                  </p>
                </div>
                </JoinReveal>
              ) : requirements?.credential_kind === "nmls" ? (
                <JoinReveal testId="join-credential-reveal">
                <div className="space-y-2 rounded-xl border border-border/70 bg-bg p-3">
                  <JoinField
                    id="join-nmls"
                    label="NMLS ID"
                    inputMode="numeric"
                    value={form.nmlsNumber}
                    onChange={(v) => update("nmlsNumber", v)}
                    placeholder="1234567"
                    required={requirements.credential_required}
                  />
                  <p className="text-xs text-fg-muted">Enter 1–7 digits. Leading zeros matter.</p>
                </div>
                </JoinReveal>
              ) : null}
              {requirementsStatus === "checking" ? (
                <p role="status" className="text-sm text-fg-muted">
                  Checking signup requirements…
                </p>
              ) : requirementsStatus === "error" ? (
                <p role="alert" className="text-sm text-destructive">
                  {REQUIREMENTS_UNAVAILABLE_MESSAGE}
                </p>
              ) : null}
              <Button
                type="submit"
                size="lg"
                className="w-full"
                disabled={requirementsStatus !== "ready"}
              >
                Create my account
              </Button>
              {message ? (
                <p role="alert" data-testid="join-error" className="text-sm text-destructive">
                  {message}
                </p>
              ) : null}
            </form>
          )}
        </div>

        <p className="mt-5 text-center text-sm text-fg-muted">
          Already have an account?{" "}
          <Link to="/login" className="font-medium text-pro underline-offset-2 hover:underline">
            Sign in
          </Link>
        </p>
      </div>
    </main>
  );
}

function JoinField({
  id,
  label,
  value,
  onChange,
  placeholder,
  required,
  type = "text",
  maxLength,
  inputMode,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  required?: boolean;
  type?: string;
  maxLength?: number;
  inputMode?: "numeric" | "text";
}) {
  return (
    <div>
      <label htmlFor={id} className="field-label">
        {label}
      </label>
      <TextField
        id={id}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        required={required}
        maxLength={maxLength}
        inputMode={inputMode}
      />
    </div>
  );
}
